import { ResourceType } from '../models/resource'
/**
 * Deterministic identity for the **auto-managed per-player master playlist** shell (tenant DO materializes
 * the empty published playlist row when this URN is first referenced, e.g. scheduling embeddings or reclaim after deleting a custom master).
 * API, player clients, and docs should use these helpers instead of string-building `player-master-*` locally.
 *
 * @see docs/plans/playlist-architecture.md
 */
import { encodeURN } from './urn'

/** Prefix for the playlist `id` segment in the master playlist URN (`urn:lucimark:playlist:<tenant>:player-master-<playerId>`). */
export const PLAYER_MASTER_PLAYLIST_ID_PREFIX = 'player-master-' as const

/** Metadata flag on published JSON marking the tenant-managed empty master playlist row for a screen (see {@link playerMasterPlaylistUrn}). */
export const PLAYER_MASTER_PLAYLIST_METADATA_FLAG = 'lucimarkPlayerMasterPlaylist' as const

/** Metadata key holding the player id alongside {@link PLAYER_MASTER_PLAYLIST_METADATA_FLAG}. */
export const PLAYER_MASTER_PLAYLIST_METADATA_PLAYER_ID_KEY = 'lucimarkPlayerId' as const

export function playerMasterPlaylistId(playerId: string): string {
  return `${PLAYER_MASTER_PLAYLIST_ID_PREFIX}${playerId}`
}

export function playerMasterPlaylistUrn(tenantId: string, playerId: string): string {
  return encodeURN({
    type: ResourceType.PLAYLIST,
    tenantId,
    id: playerMasterPlaylistId(playerId)
  })
}

/** Minimal playlist row shape for tenant listings (app simple mode, etc.). */
export interface TPlayerMasterPlaylistPlaylistLike {
  urn: string
  playlist?: { metadata?: Record<string, unknown> | null } | null
}

const PLAYLIST_URN_PREFIX = `urn:lucimark:${ResourceType.PLAYLIST}:` as const

/**
 * True when the playlist id segment uses {@link PLAYER_MASTER_PLAYLIST_ID_PREFIX}.
 * Parses `urn:lucimark:playlist:<tenantId>:<id>[:variant…]` with a playlist-specific split (tenant
 * ids are UUIDs with hyphens, which the shared `decodeURN` regex does not treat as a single segment).
 */
export function playlistUrnIsPlayerMasterPlaylist(urn: string): boolean {
  if (typeof urn !== 'string' || urn.trim() === '') {
    return false
  }
  return parsePlayerMasterPlaylistUrn(urn) != null
}

/**
 * If `urn` is the auto-managed per-player master playlist, returns tenant id and player id from the URN.
 * Uses the same segment rules as legacy checks (tenant may contain hyphens; playlist id is the first segment after `tenant:`).
 */
export function parsePlayerMasterPlaylistUrn(urn: string): { tenantId: string, playerId: string } | null {
  const u = urn.toLowerCase()
  if (!u.startsWith(PLAYLIST_URN_PREFIX)) {
    return null
  }
  const rest = u.slice(PLAYLIST_URN_PREFIX.length)
  const firstColon = rest.indexOf(':')
  if (firstColon === -1) {
    return null
  }
  const tenantId = rest.slice(0, firstColon)
  const afterTenant = rest.slice(firstColon + 1)
  if (afterTenant.length === 0) {
    return null
  }
  const playlistId = afterTenant.split(':')[0]!
  if (!playlistId.startsWith(PLAYER_MASTER_PLAYLIST_ID_PREFIX)) {
    return null
  }
  const playerId = playlistId.slice(PLAYER_MASTER_PLAYLIST_ID_PREFIX.length)
  if (playerId.length === 0) {
    return null
  }
  return { tenantId, playerId }
}

/**
 * Whether a tenant playlist listing row is the auto-managed **per-player master** playlist
 * (metadata flag and/or deterministic URN id). Prefer this over ad hoc `player-master-` string checks.
 */
export function isPlayerMasterPlaylistPlaylist(feed: TPlayerMasterPlaylistPlaylistLike): boolean {
  const urn = feed?.urn?.trim() ?? ''
  if (urn === '') {
    return false
  }
  const flagged = feed.playlist?.metadata?.[PLAYER_MASTER_PLAYLIST_METADATA_FLAG]
  if (flagged === true) {
    return true
  }
  return playlistUrnIsPlayerMasterPlaylist(urn)
}

/**
 * `player.master_playlist_urn` when it points at an **operator** playlist.
 * Returns `null` when unset or when the column still references an auto-managed `player-master-*` shell
 * (those must not appear as registration `schedule` roots).
 */
export function operatorAssignedPlayerMasterPlaylistUrn(
  masterPlaylistUrn: string | null | undefined
): string | null {
  const u = masterPlaylistUrn?.trim() ?? ''
  if (u === '' || playlistUrnIsPlayerMasterPlaylist(u)) {
    return null
  }
  return u
}

/**
 * Parent playlist URN for scheduling embeddings and simple-mode “feed on screen” wiring.
 * When `player.master_playlist_urn` is set, that playlist is the parent; otherwise the
 * canonical auto-managed `player-master-<playerId>` row.
 *
 * @param masterPlaylistUrn — Value from `player.master_playlist_urn` (API/DO); whitespace is ignored. Pass `null`/`undefined`/empty to use the canonical master.
 */
export function effectivePlayerMasterPlaylistParentUrn(
  tenantId: string,
  playerId: string,
  masterPlaylistUrn: string | null | undefined
): string {
  const u = masterPlaylistUrn?.trim()
  if (u != null && u !== '') {
    return u
  }
  return playerMasterPlaylistUrn(tenantId, playerId)
}
